import styled from 'styled-components'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'

const Card = styled(motion.div)`
  background: var(--color-bg-card);
  border: 1px solid ${({ $featured }) => ($featured ? 'var(--color-accent)' : 'var(--color-border)')};
  border-radius: 16px;
  padding: 32px;
  position: relative;
  transition: border-color 0.3s ease, transform 0.2s ease;

  ${({ $featured }) =>
    $featured &&
    `
    box-shadow: 0 0 40px var(--color-accent-glow);
  `}

  &:hover {
    transform: translateY(-4px);
    border-color: var(--color-accent);
  }
`

const Badge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end));
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 16px;
  border-radius: 20px;
`

const PlanName = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 8px;
`

const Price = styled.div`
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 4px;
  white-space: nowrap;

  span {
    font-size: 0.9rem;
    font-weight: 400;
    color: var(--color-text-secondary);
  }
`

const Description = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin-bottom: 24px;
  line-height: 1.5;
`

const FeatureList = styled.ul`
  list-style: none;
  margin-bottom: 28px;
`

const Feature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin-bottom: 10px;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    color: var(--color-success);
    margin-top: 3px;
  }
`

const Button = styled.a`
  display: block;
  text-align: center;
  padding: 14px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: ${({ $featured }) =>
    $featured
      ? 'linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end))'
      : 'transparent'};
  color: ${({ $featured }) => ($featured ? 'white' : 'var(--color-text)')};
  border: ${({ $featured }) => ($featured ? 'none' : '1px solid var(--color-border)')};

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    color: ${({ $featured }) => ($featured ? 'white' : 'var(--color-text)')};
  }
`

export default function PricingCard({ name, price, period, description, features, featured, badge, buttonText, buttonHref, delay = 0 }) {
  return (
    <Card
      $featured={featured}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
    >
      {badge && <Badge>{badge}</Badge>}
      <PlanName>{name}</PlanName>
      <Price>
        {price} <span>{period}</span>
      </Price>
      <Description>{description}</Description>
      <FeatureList>
        {features.map((feature, i) => (
          <Feature key={i}>
            <Check size={16} />
            {feature}
          </Feature>
        ))}
      </FeatureList>
      <Button href={buttonHref || '#'} $featured={featured}>
        {buttonText || 'Get Started'}
      </Button>
    </Card>
  )
}
